// Layer Reducers
// Handles layer visibility and locking operations

import { produce } from 'immer';
import { getItemByIdOrThrow } from 'src/utils';
import type { ViewReducerContext, State } from './types';
import { LayerType } from 'src/features/layers';

export interface LayerUpdate {
  id: string;
  type: LayerType;
  visible?: boolean;
  locked?: boolean;
}

/**
 * Toggle layer visibility
 */
export const toggleLayerVisibility = (
  payload: { id: string; type: LayerType },
  ctx: ViewReducerContext
): State => {
  const newState = produce(ctx.state, (draft) => {
    const view = getItemByIdOrThrow(draft.model.views, ctx.viewId);

    switch (payload.type) {
      case LayerType.ITEM: {
        const item = view.value.items.find((i) => i.id === payload.id);
        if (item) {
          item.visible = !(item.visible !== false); // Toggle, default true
        }
        break;
      }
      case LayerType.RECTANGLE: {
        const rect = view.value.rectangles?.find((r) => r.id === payload.id);
        if (rect) {
          rect.visible = !(rect.visible !== false);
        }
        break;
      }
      case LayerType.CONNECTOR: {
        const conn = view.value.connectors?.find((c) => c.id === payload.id);
        if (conn) {
          conn.visible = !(conn.visible !== false);
        }
        break;
      }
      case LayerType.TEXTBOX: {
        const textBox = view.value.textBoxes?.find((t) => t.id === payload.id);
        if (textBox) {
          textBox.visible = !(textBox.visible !== false);
        }
        break;
      }
    }
  });

  return newState;
};

/**
 * Set layer visibility
 */
export const setLayerVisibility = (
  payload: { id: string; type: LayerType; visible: boolean },
  ctx: ViewReducerContext
): State => {
  const newState = produce(ctx.state, (draft) => {
    const view = getItemByIdOrThrow(draft.model.views, ctx.viewId);

    switch (payload.type) {
      case LayerType.ITEM: {
        const item = view.value.items.find((i) => i.id === payload.id);
        if (item) {
          item.visible = payload.visible;
        }
        break;
      }
      case LayerType.RECTANGLE: {
        const rect = view.value.rectangles?.find((r) => r.id === payload.id);
        if (rect) {
          rect.visible = payload.visible;
        }
        break;
      }
      case LayerType.CONNECTOR: {
        const conn = view.value.connectors?.find((c) => c.id === payload.id);
        if (conn) {
          conn.visible = payload.visible;
        }
        break;
      }
      case LayerType.TEXTBOX: {
        const textBox = view.value.textBoxes?.find((t) => t.id === payload.id);
        if (textBox) {
          textBox.visible = payload.visible;
        }
        break;
      }
    }
  });

  return newState;
};

/**
 * Toggle layer locked state
 */
export const toggleLayerLocked = (
  payload: { id: string; type: LayerType },
  ctx: ViewReducerContext
): State => {
  const newState = produce(ctx.state, (draft) => {
    const view = getItemByIdOrThrow(draft.model.views, ctx.viewId);

    switch (payload.type) {
      case LayerType.ITEM: {
        const item = view.value.items.find((i) => i.id === payload.id);
        if (item) {
          item.locked = !item.locked;
        }
        break;
      }
      case LayerType.RECTANGLE: {
        const rect = view.value.rectangles?.find((r) => r.id === payload.id);
        if (rect) {
          rect.locked = !rect.locked;
        }
        break;
      }
      case LayerType.CONNECTOR: {
        const conn = view.value.connectors?.find((c) => c.id === payload.id);
        if (conn) {
          conn.locked = !conn.locked;
        }
        break;
      }
      case LayerType.TEXTBOX: {
        const textBox = view.value.textBoxes?.find((t) => t.id === payload.id);
        if (textBox) {
          textBox.locked = !textBox.locked;
        }
        break;
      }
    }
  });

  return newState;
};

/**
 * Set layer locked state
 */
export const setLayerLocked = (
  payload: { id: string; type: LayerType; locked: boolean },
  ctx: ViewReducerContext
): State => {
  const newState = produce(ctx.state, (draft) => {
    const view = getItemByIdOrThrow(draft.model.views, ctx.viewId);

    switch (payload.type) {
      case LayerType.ITEM: {
        const item = view.value.items.find((i) => i.id === payload.id);
        if (item) {
          item.locked = payload.locked;
        }
        break;
      }
      case LayerType.RECTANGLE: {
        const rect = view.value.rectangles?.find((r) => r.id === payload.id);
        if (rect) {
          rect.locked = payload.locked;
        }
        break;
      }
      case LayerType.CONNECTOR: {
        const conn = view.value.connectors?.find((c) => c.id === payload.id);
        if (conn) {
          conn.locked = payload.locked;
        }
        break;
      }
      case LayerType.TEXTBOX: {
        const textBox = view.value.textBoxes?.find((t) => t.id === payload.id);
        if (textBox) {
          textBox.locked = payload.locked;
        }
        break;
      }
    }
  });

  return newState;
};

/**
 * Show all layers
 */
export const showAllLayers = (
  _payload: undefined,
  ctx: ViewReducerContext
): State => {
  const newState = produce(ctx.state, (draft) => {
    const view = getItemByIdOrThrow(draft.model.views, ctx.viewId);

    view.value.items.forEach((item) => {
      item.visible = true;
    });

    view.value.rectangles?.forEach((rect) => {
      rect.visible = true;
    });

    view.value.connectors?.forEach((conn) => {
      conn.visible = true;
    });

    view.value.textBoxes?.forEach((textBox) => {
      textBox.visible = true;
    });
  });

  return newState;
};

/**
 * Hide all layers
 */
export const hideAllLayers = (
  _payload: undefined,
  ctx: ViewReducerContext
): State => {
  const newState = produce(ctx.state, (draft) => {
    const view = getItemByIdOrThrow(draft.model.views, ctx.viewId);

    view.value.items.forEach((item) => {
      item.visible = false;
    });

    view.value.rectangles?.forEach((rect) => {
      rect.visible = false;
    });

    view.value.connectors?.forEach((conn) => {
      conn.visible = false;
    });

    view.value.textBoxes?.forEach((textBox) => {
      textBox.visible = false;
    });
  });

  return newState;
};

/**
 * Lock all layers
 */
export const lockAllLayers = (
  _payload: undefined,
  ctx: ViewReducerContext
): State => {
  const newState = produce(ctx.state, (draft) => {
    const view = getItemByIdOrThrow(draft.model.views, ctx.viewId);

    view.value.items.forEach((item) => {
      item.locked = true;
    });

    view.value.rectangles?.forEach((rect) => {
      rect.locked = true;
    });

    view.value.connectors?.forEach((conn) => {
      conn.locked = true;
    });

    view.value.textBoxes?.forEach((textBox) => {
      textBox.locked = true;
    });
  });

  return newState;
};

/**
 * Unlock all layers
 */
export const unlockAllLayers = (
  _payload: undefined,
  ctx: ViewReducerContext
): State => {
  const newState = produce(ctx.state, (draft) => {
    const view = getItemByIdOrThrow(draft.model.views, ctx.viewId);

    view.value.items.forEach((item) => {
      item.locked = false;
    });

    view.value.rectangles?.forEach((rect) => {
      rect.locked = false;
    });

    view.value.connectors?.forEach((conn) => {
      conn.locked = false;
    });

    view.value.textBoxes?.forEach((textBox) => {
      textBox.locked = false;
    });
  });

  return newState;
};
